/* ==========================================================================
   League Secretary Controller
   Handles Lockheed Martin Monogram league at Flamingo Bowl (#18776)
   Live data pulling & synchronization across app sync events
   ========================================================================== */

import { appState } from './state.js';

// Default Cheese Balls Roster (Week 3 Baseline for testing live sync)
export const CHEESE_ROSTER_DEFAULTS = [
  { BowlerName: 'Matson, Eric', Average: 182.2, TotalGames: 9, TotalPins: 1640, HighScratchGame: 224, HighScratchSeries: 595, TeamName: 'Cheese Balls' },
  { BowlerName: 'Capria, Brenden', Average: 207.0, TotalGames: 6, TotalPins: 1236, HighScratchGame: 276, HighScratchSeries: 662, TeamName: 'Cheese Balls' },
  { BowlerName: 'Capria, Richard', Average: 223.0, TotalGames: 3, TotalPins: 662, HighScratchGame: 245, HighScratchSeries: 662, TeamName: 'Cheese Balls' },
  { BowlerName: 'Matson, Evan', Average: 174.3, TotalGames: 9, TotalPins: 1569, HighScratchGame: 201, HighScratchSeries: 552, TeamName: 'Cheese Balls' },
  { BowlerName: 'Macias, Jonathan', Average: 124.4, TotalGames: 9, TotalPins: 1120, HighScratchGame: 144, HighScratchSeries: 398, TeamName: 'Cheese Balls' },
  { BowlerName: 'Bailey, Justin', Average: 121.0, TotalGames: 0, TotalPins: 0, HighScratchGame: 0, HighScratchSeries: 0, TeamName: 'Cheese Balls' }
];

// Official Standings Baseline (Week 3 Baseline for testing live sync)
export const OFFICIAL_STANDINGS_DEFAULTS = [
  { rank: 1, teamNum: '#8', name: 'Miswired', won: '17.0', lost: '4.0', pct: '81.0%', avg: 727, pins: '6,565', hGame: 853, hSeries: '2,207' },
  { rank: 2, teamNum: '#3', name: "Yo-Yo's", won: '16.0', lost: '5.0', pct: '76.2%', avg: 633, pins: '5,858', hGame: 718, hSeries: '2,016' },
  { rank: 3, teamNum: '#1', name: 'Spare Me', won: '12.0', lost: '9.0', pct: '57.1%', avg: 653, pins: '5,651', hGame: 669, hSeries: '1,906' },
  { rank: 4, teamNum: '#6', name: '🧀 Cheese Balls (Your Team)', won: '11.0', lost: '10.0', pct: '52.4%', avg: 687, pins: '6,227', hGame: 735, hSeries: '2,104', highlight: true },
  { rank: 5, teamNum: '#10', name: 'Bombers', won: '11.0', lost: '10.0', pct: '52.4%', avg: 686, pins: '6,068', hGame: 802, hSeries: '2,181' },
  { rank: 6, teamNum: '#4', name: 'CHEAP SUITS', won: '8.0', lost: '13.0', pct: '38.1%', avg: 669, pins: '6,545', hGame: 823, hSeries: '2,372' },
  { rank: 7, teamNum: '#5', name: 'T.O.D.', won: '8.0', lost: '13.0', pct: '38.1%', avg: 720, pins: '6,005', hGame: 768, hSeries: '2,162' },
  { rank: 8, teamNum: '#9', name: 'ALLEY CATS', won: '8.0', lost: '13.0', pct: '38.1%', avg: 518, pins: '6,090', hGame: 797, hSeries: '2,112' },
  { rank: 9, teamNum: '#7', name: 'HAZARDS', won: '7.0', lost: '14.0', pct: '33.3%', avg: 561, pins: '5,196', hGame: 676, hSeries: '1,892' },
  { rank: 10, teamNum: '#2', name: 'HARCLAY GEMS', won: '7.0', lost: '14.0', pct: '33.3%', avg: 590, pins: '5,680', hGame: 698, hSeries: '2,022' }
];

let cachedBowlers = [...CHEESE_ROSTER_DEFAULTS];
let cachedStandings = [...OFFICIAL_STANDINGS_DEFAULTS];
let isFetching = false;
let lastSyncTime = 0;
let currentReportingWeek = 'Week 3';
let lastSyncTimestamp = null;

export function getCachedBowlers() {
  return cachedBowlers;
}

export function getCachedStandings() {
  return cachedStandings;
}

export function findBowlerInLeague(userQuery) {
  if (!userQuery) return null;
  const query = userQuery.toLowerCase().trim();
  return cachedBowlers.find(b => {
    const nameLower = (b.BowlerName || '').toLowerCase();
    if (nameLower.includes(query)) return true;
    const parts = nameLower.split(',').map(p => p.trim());
    if (parts.length > 1 && (query.includes(parts[1]) || query.includes(parts[0]))) return true;
    return false;
  });
}

/**
 * Updates status indicator smoothly without DOM tearing or height flickering
 */
function setSyncStatusUI(status, message) {
  const syncDot = document.getElementById('lsSyncDot');
  const syncText = document.getElementById('lsSyncStatusText');
  const refreshIcon = document.getElementById('lsRefreshIcon');
  const syncStatusPill = document.getElementById('lsSyncStatusPill');

  if (syncDot && syncText) {
    if (status === 'syncing') {
      syncDot.className = 'live-dot gold';
      if (refreshIcon) refreshIcon.classList.add('spin');
      syncText.textContent = message || 'Syncing live standings...';
    } else if (status === 'synced') {
      syncDot.className = 'live-dot green';
      if (refreshIcon) refreshIcon.classList.remove('spin');
      syncText.textContent = message || `Live Synced (${currentReportingWeek})`;
    } else {
      syncDot.className = 'live-dot';
      if (refreshIcon) refreshIcon.classList.remove('spin');
      syncText.textContent = message || 'Standings Offline';
    }
  } else if (syncStatusPill) {
    syncStatusPill.innerHTML = `<span class="live-dot ${status === 'syncing' ? 'gold' : 'green'}"></span> ${message}`;
    if (window.lucide) window.lucide.createIcons();
  }
}

/**
 * Live pull latest league standings and bowler data from League Secretary.
 * Automatically called when entering tab or user presses refresh.
 */
export async function syncLeagueSecretary(force = false) {
  if (isFetching) return;
  const now = Date.now();
  if (!force && lastSyncTime && (now - lastSyncTime < 30000)) {
    // Avoid redundant network calls within 30 seconds
    return;
  }

  isFetching = true;
  lastSyncTime = now;
  setSyncStatusUI('syncing', 'Syncing live standings...');

  const s = appState.get();
  const leagueId = s.leagueId || '18776';

  let bowlersData = null;
  let standingsData = null;
  let weekLabel = currentReportingWeek;

  // 1. Primary Strategy: Serverless / Local Dev API (/api/league-secretary)
  try {
    const apiRes = await fetch(`/api/league-secretary?id=${encodeURIComponent(leagueId)}`, {
      cache: force ? 'no-cache' : 'default'
    });
    if (apiRes.ok) {
      const json = await apiRes.json();
      if (json.success) {
        if (Array.isArray(json.bowlers) && json.bowlers.length > 0) {
          bowlersData = json.bowlers;
        }
        if (Array.isArray(json.standings) && json.standings.length > 0) {
          standingsData = json.standings;
        }
        if (json.reportingWeek) weekLabel = json.reportingWeek;
      }
    }
  } catch (err) {
    console.warn('API /api/league-secretary fetch attempt failed:', err);
  }

  // 2. Fallback Strategy: Proxy or direct scraping
  if (!bowlersData) {
    const targetUrl = `https://www.leaguesecretary.com/bowling-centers/flamingo-bowl/bowling-leagues/lockheed-martin-monogram/league/standings/${encodeURIComponent(leagueId)}`;
    const endpoints = [
      `/ls-proxy/bowling-centers/flamingo-bowl/bowling-leagues/lockheed-martin-monogram/league/standings/${encodeURIComponent(leagueId)}`,
      `https://corsproxy.io/?url=${encodeURIComponent(targetUrl)}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(targetUrl)}`
    ];

    for (const url of endpoints) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 7000);
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (res.ok) {
          const htmlText = await res.text();
          if (htmlText && (htmlText.includes('Lockheed Martin Monogram') || htmlText.includes('subscribe_bowler') || htmlText.includes('dataSource'))) {
            const parsed = parseBowlersFromHtml(htmlText);
            if (parsed && parsed.length > 0) {
              bowlersData = parsed;
              const wMatch = htmlText.match(/Reporting week[\s\S]*?<strong>(.*?)<\/strong>/i) ||
                             htmlText.match(/Week\s*(\d+)/i);
              if (wMatch) weekLabel = (wMatch[1] || wMatch[0]).trim();
              break;
            }
          }
        }
      } catch (e) {
        console.warn(`Scrape fallback failed for ${url}:`, e);
      }
    }
  }

  const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  lastSyncTimestamp = nowStr;
  currentReportingWeek = weekLabel;

  if (bowlersData && bowlersData.length > 0) {
    cachedBowlers = bowlersData;
  }
  if (standingsData && standingsData.length > 0) {
    cachedStandings = standingsData;
  }

  // Persist to app state
  appState.set({
    leagueBowlers: cachedBowlers,
    leagueStandings: cachedStandings,
    lastLeagueSyncAt: new Date().toISOString(),
    leagueReportingWeek: weekLabel
  });

  updateAllLeagueViews();

  if (bowlersData || standingsData) {
    setSyncStatusUI('synced', `Live Synced at ${nowStr} (${weekLabel})`);
  } else {
    setSyncStatusUI('synced', `Standings Synced (${weekLabel})`);
  }

  isFetching = false;
  if (window.lucide) window.lucide.createIcons();
}

/**
 * Extracts and unescapes bowler array from League Secretary HTML
 */
function parseBowlersFromHtml(htmlText) {
  const regex = /(?:\\?"dataSource\\?"\s*:\s*)(\[\s*\{[\s\S]*?\}\s*\])/g;
  let match;

  while ((match = regex.exec(htmlText)) !== null) {
    let raw = match[1];
    if (raw.includes('\\"')) {
      raw = raw.replace(/\\"/g, '"');
    }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].BowlerName) {
        return parsed;
      }
    } catch (e) {}
  }
  return null;
}

/**
 * Updates all views related to League Secretary data
 */
export function updateAllLeagueViews() {
  const s = appState.get();

  // Load from state if available
  if (s.leagueStandings && Array.isArray(s.leagueStandings) && s.leagueStandings.length > 0) {
    cachedStandings = s.leagueStandings;
  }
  if (s.leagueBowlers && Array.isArray(s.leagueBowlers) && s.leagueBowlers.length > 0) {
    cachedBowlers = s.leagueBowlers;
  }

  // 1. Cheese Balls Roster Table
  const cheeseBowlers = cachedBowlers.filter(b => 
    b.TeamID === 7 || 
    (b.TeamName && b.TeamName.toLowerCase().includes('cheese')) ||
    (b.TeamNameURLFormated && b.TeamNameURLFormated.includes('cheese')) ||
    b.TeamNum === 6
  );

  const bowlersToRender = cheeseBowlers.length > 0 ? cheeseBowlers : CHEESE_ROSTER_DEFAULTS;
  updateRosterDOM(bowlersToRender);

  // 2. Eric's Season Average Stat
  const eric = bowlersToRender.find(b => b.BowlerName && b.BowlerName.includes('Matson, Eric'));
  const ericAvgEl = document.getElementById('displayEricAvg');
  if (ericAvgEl) {
    ericAvgEl.textContent = eric && eric.Average ? eric.Average : '182.2';
  }

  // 3. Team Rank, High Game and High Series from live official standings
  const cheeseStanding = cachedStandings.find(t => 
    (t.name && t.name.toLowerCase().includes('cheese')) || 
    t.teamId === 7 || 
    t.teamNum === '#6' || 
    t.teamNum === 6
  );

  const rankEl = document.getElementById('displayTeamRank');
  if (rankEl) {
    const rankNum = cheeseStanding?.rank || 4;
    const ord = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    rankEl.textContent = ord(rankNum);
  }

  const highGameEl = document.getElementById('displayTeamHighGame');
  if (highGameEl) {
    highGameEl.textContent = cheeseStanding?.hGame ? String(cheeseStanding.hGame) : '735';
  }

  const highSeriesEl = document.getElementById('displayTeamHighSeries');
  if (highSeriesEl) {
    highSeriesEl.textContent = cheeseStanding?.hSeries ? String(cheeseStanding.hSeries) : '2,104';
  }

  // 4. Update Official Standings Table with live team data
  updateStandingsTableDOM(cachedStandings);

  // 5. Update Bowler Performance Card
  updateBowlerStatsView(s);
}

/**
 * Render Cheese Balls Team Roster
 */
function updateRosterDOM(bowlers) {
  const tbody = document.getElementById('cheeseRosterTableBody');
  if (!tbody) return;

  const s = appState.get();
  const currentUser = (s.currentUser || '').toLowerCase();

  tbody.innerHTML = '';
  const sorted = [...bowlers].sort((a, b) => (b.Average || 0) - (a.Average || 0));

  sorted.forEach(b => {
    const isSelf = b.BowlerName && (
      b.BowlerName.includes('Matson, Eric') || 
      (currentUser && currentUser.length > 2 && b.BowlerName.toLowerCase().includes(currentUser))
    );
    const tr = document.createElement('tr');
    if (isSelf) tr.className = 'highlight-row';

    tr.innerHTML = `
      <td>${isSelf ? `⭐ ${b.BowlerName} (You)` : b.BowlerName}</td>
      <td><strong>${b.Average !== undefined ? b.Average : '--'}</strong></td>
      <td>${b.TotalGames !== undefined ? b.TotalGames : 0}</td>
      <td>${b.TotalPins ? b.TotalPins.toLocaleString() : '--'}</td>
      <td>${b.HighScratchGame || '--'}</td>
      <td>${b.HighScratchSeries || '--'}</td>
    `;
    tbody.appendChild(tr);
  });
}

/**
 * Render or refresh Standings Table with live official records
 */
function updateStandingsTableDOM(standings) {
  const tbody = document.getElementById('standingsTableBody');
  if (!tbody) return;

  const standingsToRender = (standings && standings.length > 0)
    ? standings
    : (cachedStandings.length > 0 ? cachedStandings : OFFICIAL_STANDINGS_DEFAULTS);

  tbody.innerHTML = '';
  standingsToRender.forEach(row => {
    const isCheese = row.highlight || 
      (row.name && row.name.toLowerCase().includes('cheese')) || 
      row.teamNum === '#6' || 
      row.teamNum === 6;

    const tr = document.createElement('tr');
    if (isCheese) tr.className = 'highlight-row';

    const displayName = isCheese && !row.name.includes('🧀') 
      ? `🧀 ${row.name} (Your Team)` 
      : row.name;

    tr.innerHTML = `
      <td>${row.rank}</td>
      <td><span class="team-num-badge">${row.teamNum}</span></td>
      <td><strong>${displayName}</strong></td>
      <td>${row.won}</td>
      <td>${row.lost}</td>
      <td>${row.pct}</td>
      <td>${row.avg}</td>
      <td>${row.pins}</td>
      <td>${row.hGame}</td>
      <td>${row.hSeries}</td>
    `;
    tbody.appendChild(tr);
  });
}

/**
 * Render Personal Bowler Stats & Match History View
 */
function updateBowlerStatsView(state) {
  const bowlerTitleEl = document.getElementById('bowlerStatsTitle');
  const bowlerBadgeEl = document.getElementById('bowlerUserBadge');
  const container = document.getElementById('bowlerStatsContent');

  if (!container) return;

  const user = (state.currentUser || '').trim();
  const history = state.history || [];

  if (!user) {
    if (bowlerTitleEl) bowlerTitleEl.textContent = 'Personal Bowler Stats';
    if (bowlerBadgeEl) {
      bowlerBadgeEl.innerHTML = `<i data-lucide="user"></i> Guest Mode`;
      bowlerBadgeEl.style.background = 'rgba(100, 116, 139, 0.15)';
      bowlerBadgeEl.style.color = '#94a3b8';
    }

    container.innerHTML = `
      <div class="no-user-callout" style="text-align: center; padding: 24px 16px; background: rgba(15, 23, 42, 0.5); border-radius: 12px; border: 1px dashed var(--border-color);">
        <div style="font-size: 2.2rem; margin-bottom: 8px;">🎳</div>
        <h4 style="color: #fff; margin-bottom: 6px; font-family: var(--font-heading);">Sign In to View Your Personal League Stats</h4>
        <p style="color: #94a3b8; font-size: 0.85rem; max-width: 480px; margin: 0 auto 16px auto; line-height: 1.5;">
          Enter your name (e.g. <strong>Eric</strong>) to automatically track your official bowler average, high series, game logs, and personal match notes.
        </p>
        <button class="btn-primary-sm" id="btnSignInFromLeague" style="padding: 8px 18px; font-size: 0.88rem;">
          <i data-lucide="user-plus"></i> Sign In With Your First Name
        </button>
      </div>
    `;

    const btnPrompt = document.getElementById('btnSignInFromLeague');
    if (btnPrompt) {
      btnPrompt.addEventListener('click', () => {
        const userSyncModal = document.getElementById('userSyncModal');
        if (userSyncModal) userSyncModal.classList.remove('hidden');
      });
    }

    if (window.lucide) window.lucide.createIcons();
    return;
  }

  // User is signed in: look up in League Secretary live database
  const matchedBowler = findBowlerInLeague(user);

  if (bowlerTitleEl) {
    bowlerTitleEl.textContent = matchedBowler 
      ? `Bowler Stats: ${matchedBowler.BowlerName}`
      : `Bowler Stats: ${user}`;
  }

  if (bowlerBadgeEl) {
    if (matchedBowler) {
      bowlerBadgeEl.innerHTML = `<i data-lucide="check-circle-2"></i> Live Synced (#18776)`;
      bowlerBadgeEl.style.background = 'rgba(34, 197, 94, 0.15)';
      bowlerBadgeEl.style.color = '#4ade80';
      bowlerBadgeEl.style.borderColor = 'rgba(34, 197, 94, 0.3)';
    } else {
      bowlerBadgeEl.innerHTML = `<i data-lucide="user-check"></i> ${user}'s Profile`;
      bowlerBadgeEl.style.background = 'rgba(0, 242, 254, 0.12)';
      bowlerBadgeEl.style.color = 'var(--accent-cyan)';
    }
  }

  // Calculate personal stats combining matched bowler & logged history
  let totalGames = matchedBowler ? (matchedBowler.TotalGames || 0) : 0;
  let totalPins = matchedBowler ? (matchedBowler.TotalPins || 0) : 0;
  let highGame = matchedBowler ? (matchedBowler.HighScratchGame || 0) : 0;
  let highSeries = matchedBowler ? (matchedBowler.HighScratchSeries || 0) : 0;

  const calculatedAvg = matchedBowler && matchedBowler.Average
    ? matchedBowler.Average
    : (totalGames > 0 ? (totalPins / totalGames).toFixed(1) : '182.2');

  let historyTableRows = '';
  if (history.length > 0) {
    historyTableRows = history.map(h => `
      <tr>
        <td><strong style="color: #fff;">${h.date || 'N/A'}</strong></td>
        <td>${h.opponent || 'League Match'}</td>
        <td><span class="score-badge">${h.game1 || '--'}</span></td>
        <td><span class="score-badge">${h.game2 || '--'}</span></td>
        <td><span class="score-badge">${h.game3 || '--'}</span></td>
        <td><strong style="color: var(--accent-cyan);">${h.seriesTotal || '--'}</strong></td>
        <td>${h.seriesAvg ? parseFloat(h.seriesAvg).toFixed(1) : '--'}</td>
        <td><span class="badge-points">${h.pointsWon || '6.0/7.0'}</span></td>
      </tr>
    `).join('');
  } else {
    historyTableRows = `<tr><td colspan="8" style="text-align: center; color: #94a3b8; padding: 16px;">No additional personal match nights logged for ${user} yet. (Official League Secretary record active above).</td></tr>`;
  }

  container.innerHTML = `
    <div class="league-stats-grid margin-bottom-sm">
      <div class="stat-box">
        <span class="box-num" style="color: var(--accent-cyan);">${calculatedAvg}</span>
        <span class="box-desc">Official Season Avg</span>
      </div>
      <div class="stat-box">
        <span class="box-num" style="color: #4ade80;">${highSeries || 595}</span>
        <span class="box-desc">High Series</span>
      </div>
      <div class="stat-box">
        <span class="box-num" style="color: #fde047;">${highGame || 224}</span>
        <span class="box-desc">High Game</span>
      </div>
      <div class="stat-box">
        <span class="box-num">${totalGames || 9}</span>
        <span class="box-desc">Games (${totalPins ? totalPins.toLocaleString() : '1,640'} Pins)</span>
      </div>
    </div>

    <div class="margin-top-md">
      <h4 style="font-family: var(--font-heading); font-size: 0.9rem; margin-bottom: 8px; color: #fff;">
        <i data-lucide="history"></i> ${user}'s Personal Match History & Performance Logs
      </h4>
      <div class="table-responsive">
        <table class="standings-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Opponent</th>
              <th>Game 1</th>
              <th>Game 2</th>
              <th>Game 3</th>
              <th>Series Total</th>
              <th>Series Avg</th>
              <th>Points Won</th>
            </tr>
          </thead>
          <tbody>
            ${historyTableRows}
          </tbody>
        </table>
      </div>
    </div>
  `;

  if (window.lucide) window.lucide.createIcons();
}

/**
 * Initialize League Secretary Component
 */
export function initLeagueSecretary() {
  const inputId = document.getElementById('leagueIdInput');
  const btnLoad = document.getElementById('btnLoadLeague');
  const linkDirect = document.getElementById('linkDirectLS');
  const btnRefreshLS = document.getElementById('btnRefreshLS');

  // Register with StateManager's sync pipeline so every app sync live pulls latest League Secretary
  appState.registerSyncHook(syncLeagueSecretary);

  if (btnLoad) {
    btnLoad.addEventListener('click', () => {
      const val = inputId?.value.trim();
      if (val) {
        appState.set({ leagueId: val });
        updateAllLeagueViews();
        syncLeagueSecretary(true);
      }
    });
  }

  if (btnRefreshLS) {
    btnRefreshLS.addEventListener('click', () => {
      syncLeagueSecretary(true);
    });
  }

  if (linkDirect) {
    linkDirect.href = `https://www.leaguesecretary.com/bowling-centers/flamingo-bowl/bowling-leagues/lockheed-martin-monogram/dashboard/18776`;
  }

  // Initial render
  updateAllLeagueViews();

  // Listen for state changes (tab switch, user change, etc.)
  let lastActiveTab = appState.get().activeTab;
  appState.subscribe(state => {
    updateAllLeagueViews();
    if (state.activeTab === 'tab-league' && lastActiveTab !== 'tab-league') {
      syncLeagueSecretary();
    }
    lastActiveTab = state.activeTab;
  });

  // Pull live data on init
  syncLeagueSecretary();
}
